import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';
import { IOneToOneTutorRateVersion } from '../OneToOneTutorRateVersionsSlice';
import { IOneToOneTutorRateStudentType } from '../OneToOneTutorRateStudentTypesSlice';
import { IUser } from '../../UserSlice';
import { RootState } from '../..';
import {
  adminCreateOneToOneTutorRate,
  adminDeleteOneToOneTutorRate,
  adminGetOneToOneTutorRates,
  adminUpdateOneToOneTutorRate,
  getUserOneToOneTutorRatesAsAdmin,
} from './adminApi';
import { getOneToOneTutorRates, getTutorRatesForAStudent } from './api';

export interface IOneToOneTutorRateAmount {
  _id: string;
  rateVersion: string | IOneToOneTutorRateVersion;
  amount: number;
  currency: string;
}

export interface IOneToOneTutorRate {
  _id: string;
  tutorCountry: string;
  amounts: IOneToOneTutorRateAmount[];
  studentType: string | IOneToOneTutorRateStudentType;
  createdBy: string | IUser;
  updatedBy: string | IUser;
}

export interface IInitialOneToOneTutorRatesState {
  status: 'idle' | 'loading' | 'rejected';
  oneToOneTutorRates: IOneToOneTutorRate[];
  tutorRateForAStudent?: {
    amount: number;
    currency: string;
  };
  // Admin
  adminOneToOneTutorRateListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<IOneToOneTutorRate>;
  };
  adminUserOneToOneTutorRates: IOneToOneTutorRate[];
}

const initialState: IInitialOneToOneTutorRatesState = {
  status: 'idle',
  oneToOneTutorRates: [],
  tutorRateForAStudent: undefined,
  // Admin
  adminOneToOneTutorRateListData: undefined,
  adminUserOneToOneTutorRates: [],
};

export const OneToOneTutorRatesSlice = createSlice({
  initialState,
  name: 'oneToOneTutorRates',
  reducers: {
    resetOneToOneTutorRates: (state) => {
      state.oneToOneTutorRates = [];
    },
    resetTutorRateForAStudent: (state) => {
      state.tutorRateForAStudent = undefined;
    },
    // Admin
    resetAdminOneToOneTutorRateListData: (state) => {
      state.adminOneToOneTutorRateListData = undefined;
    },
    resetAdminUserOneToOneTutorRates: (state) => {
      state.adminUserOneToOneTutorRates = [];
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getOneToOneTutorRates.fulfilled, (state, action) => {
        const result = action.payload;

        state.oneToOneTutorRates = result;
      })
      .addCase(getTutorRatesForAStudent.fulfilled, (state, action) => {
        const result = action.payload;

        state.tutorRateForAStudent = result;
      });

    builder
      // Admin
      .addCase(adminGetOneToOneTutorRates.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminOneToOneTutorRateListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminOneToOneTutorRateListData = newAdminOneToOneTutorRateListData;
      })
      .addCase(adminCreateOneToOneTutorRate.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminOneToOneTutorRateListData) {
          const newAdminOneToOneTutorRateListData = {
            ...state.adminOneToOneTutorRateListData,
          };

          newAdminOneToOneTutorRateListData.list.unshift(result);

          if (newAdminOneToOneTutorRateListData.totalDocs) {
            newAdminOneToOneTutorRateListData.totalDocs += 1;
          } else {
            newAdminOneToOneTutorRateListData.totalDocs = 1;
          }

          state.adminOneToOneTutorRateListData = newAdminOneToOneTutorRateListData;
        }
      })
      .addCase(adminUpdateOneToOneTutorRate.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminOneToOneTutorRateListData &&
          state.adminOneToOneTutorRateListData.list &&
          state.adminOneToOneTutorRateListData.list.length
        ) {
          const newAdminOneToOneTutorRateListData = {
            ...state.adminOneToOneTutorRateListData,
          };

          const index = newAdminOneToOneTutorRateListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminOneToOneTutorRateListData.list[index] = result;
          }

          state.adminOneToOneTutorRateListData = newAdminOneToOneTutorRateListData;
        }
      })
      .addCase(adminDeleteOneToOneTutorRate.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminOneToOneTutorRateListData &&
          state.adminOneToOneTutorRateListData.list &&
          state.adminOneToOneTutorRateListData.list.length
        ) {
          const newAdminOneToOneTutorRateListData = {
            ...state.adminOneToOneTutorRateListData,
          };

          newAdminOneToOneTutorRateListData.totalDocs = newAdminOneToOneTutorRateListData.totalDocs
            ? newAdminOneToOneTutorRateListData.totalDocs - 1
            : 0;

          newAdminOneToOneTutorRateListData.list = newAdminOneToOneTutorRateListData.list.filter(
            (item) => item._id !== result._id
          );

          state.adminOneToOneTutorRateListData = newAdminOneToOneTutorRateListData;
        }
      })
      .addCase(getUserOneToOneTutorRatesAsAdmin.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminUserOneToOneTutorRates = result;
      });

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        getOneToOneTutorRates,
        getTutorRatesForAStudent,
        // Admin
        adminCreateOneToOneTutorRate,
        adminGetOneToOneTutorRates,
        adminUpdateOneToOneTutorRate,
        adminDeleteOneToOneTutorRate,
        getUserOneToOneTutorRatesAsAdmin
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isPending(
        getOneToOneTutorRates,
        getTutorRatesForAStudent,
        // Admin
        adminCreateOneToOneTutorRate,
        adminGetOneToOneTutorRates,
        adminUpdateOneToOneTutorRate,
        adminDeleteOneToOneTutorRate,
        getUserOneToOneTutorRatesAsAdmin
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        getOneToOneTutorRates,
        getTutorRatesForAStudent,
        // Admin
        adminCreateOneToOneTutorRate,
        adminGetOneToOneTutorRates,
        adminUpdateOneToOneTutorRate,
        adminDeleteOneToOneTutorRate,
        getUserOneToOneTutorRatesAsAdmin
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getOneToOneTutorRatesState = (
  state: RootState
): RootState['oneToOneTutorRates'] => state.oneToOneTutorRates;

export const {
  resetOneToOneTutorRates,
  resetTutorRateForAStudent,
  // Admin
  resetAdminOneToOneTutorRateListData,
  resetAdminUserOneToOneTutorRates,
} = OneToOneTutorRatesSlice.actions;

export * from './api';
export * from './adminApi';
export default OneToOneTutorRatesSlice.reducer;
